import { GetAssessmentQuery } from "types/graphql-schema";

import { formatNameList } from "@apps/programs/helpers";
import useLabel from "@apps/use-label/use-label";
import Callout from "@components/callout/callout";

type OtherManagerReviewers = NonNullable<
  GetAssessmentQuery["assessment"]
>["otherManagerReviewers"];

/**
 * A person with several managers is owed one manager review, and every manager is asked.
 * Tells the writer who else can write it, or that someone already did, so they coordinate.
 */
const AssessmentOtherManagersCallout = ({
  otherManagerReviewers,
  targetName,
}: {
  otherManagerReviewers: OtherManagerReviewers;
  targetName: string;
}) => {
  const label = useLabel();
  const submitted = otherManagerReviewers.filter(
    (reviewer) => reviewer.submittedDatetime,
  );
  const names = (reviewers: OtherManagerReviewers) =>
    formatNameList(reviewers.map((reviewer) => reviewer.user.name));

  if (submitted.length > 0) {
    return (
      <Callout
        title={`This ${label("review")} is already submitted`}
        theme="warning"
        className="mt-4"
        icon="info"
      >
        {names(submitted)} already submitted this {label("review")}. If you
        submit yours too, {targetName} gets two manager{" "}
        {label("review", { pluralize: true })}.
      </Callout>
    );
  }
  if (otherManagerReviewers.length > 0) {
    return (
      <Callout
        title={`Other managers can write this ${label("review")}`}
        theme="default"
        className="mt-4"
        icon="info"
      >
        {names(otherManagerReviewers)} can also write this {label("review")}.
        The first manager to submit completes it.
      </Callout>
    );
  }
  return null;
};

export default AssessmentOtherManagersCallout;
